import { Component, ElementRef, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  UntypedFormArray,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DirtyComponent } from 'projects/shared/src/helpers/CanDeactivate/dirty-component';
import { TranslateService } from '@ngx-translate/core';
import { SwalService } from 'projects/shared/src/services/swal.service';
import { EvaluationTaskService } from '../../../services/evaluation-task.service';

import Swal from 'sweetalert2';
import { QuestionsTask } from 'projects/admin/src/app/features/ambassador-evaluation-tasks/models/QuestionsTask';
import { QuestionTypeCodeEnum } from 'projects/admin/src/app/shared/models/enums/QuestionTypeEnum';
import { EvaluationAnswerDto } from 'projects/admin/src/app/features/ambassador-evaluation-tasks/models/EvaluationAnswerDto';
import { Observable } from 'rxjs';

export interface DialogData {
  taskId: any;
}

@Component({
  selector: 'app-evaluation-task-survey',
  standalone: false,
  templateUrl: './evaluation-task-survey.component.html'
})
export class EvaluationTaskSurveyComponent implements OnInit {
  selectedIndex = 0;
  questionsList: QuestionsTask[] = [];
  isSubmitted = false;
  loading = false;
  fetching = false;
  questionTypeCodeEnum = QuestionTypeCodeEnum
  taskId!: number;

  formGroup: FormGroup = new FormGroup({});

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private dialogRef: MatDialogRef<EvaluationTaskSurveyComponent>,
    private formBuilder: FormBuilder,
    private service: EvaluationTaskService,
    private el: ElementRef,
    private translate: TranslateService,
    private swalService: SwalService
  ) {
  }

  ngOnInit() {
    this.taskId = this.data.taskId;
    this.buildForm();
    this.fetchQuestions();
    // this.close.subscribe((isClosed) => {
    //   if (isClosed) {
    //     this.saveTaskDraftAnswers()
    //   }
    // });
  }


  fetchQuestions() {
    this.fetching = true;
    this.service.getQuestionsByTaskId(this.taskId).subscribe({
      next: (res: QuestionsTask[]) => {
        this.questionsList = [...res].sort((a, b) => a.order - b.order);
        this.bindData();
        this.fetching = false;
      },
      error: () => {
        this.fetching = false;
      }
    });
  }

  buildForm() {
    this.formGroup = this.formBuilder.group({
      items: this.formBuilder.array([])
    });
  }

  get items() {
    return this.formGroup.get('items') as UntypedFormArray;
  }

  bindData() {
    this.questionsList.forEach((question) => {
      this.addItem(question);
    });
  }


  addItem(question: QuestionsTask) {
    const filesControl = this.formBuilder.control(question.attachmentUrls || []);

    const requiredWhenFileUploaded = (control: AbstractControl): ValidationErrors | null => {
      const hasAnswer = !!control.value;
      const hasFiles = !!(filesControl.value as any[])?.length;
      if (!question.isRequired && question.isAttachmentsRequired && hasFiles && !hasAnswer) {
        return { required: true };
      }
      return null;
    };

    const group: { [key: string]: any } = {
      questionDetails: [question],
      files: filesControl,
      deletedAttachmentIds: [[]]
    };


    switch (question.questionTypeCode) {
      case QuestionTypeCodeEnum.Text_Area:
        group['answer'] = [
          question.textAnswer,
          [...(question.isRequired ? [Validators.required] : []), requiredWhenFileUploaded]
        ];
        break;

      case QuestionTypeCodeEnum.Star_Rating:
        group['answer'] = [
          question.starRating,
          [...(question.isRequired ? [Validators.required] : []), requiredWhenFileUploaded]
        ];
        break;

      case QuestionTypeCodeEnum.Single_Choice:
        group['answer'] = [
          question.selectedOptionId,
          [...(question.isRequired ? [Validators.required] : []), requiredWhenFileUploaded]
        ];
        break;

      case QuestionTypeCodeEnum.Multiple_Choice:
        group['options'] = this.formBuilder.array(
          (question.questionOptions || []).map((opt) =>
            this.formBuilder.control(question.selectedOptionIds?.includes(opt.optionId) ?? false)
          )
        );
        break;
    }

    const itemGroup = this.formBuilder.group(group, {
      validators: this.questionValidator()
    });

    filesControl.valueChanges.subscribe(() => {
      itemGroup.get('answer')?.updateValueAndValidity({ emitEvent: false });
      itemGroup.updateValueAndValidity({ emitEvent: false, onlySelf: true });
    });

    this.items.push(itemGroup);
  }

  getOptionsArray(item: AbstractControl): FormArray {
    return item.get('options') as FormArray;
  }

  getQuestionDetails(item: AbstractControl): QuestionsTask {
    return item.get('questionDetails')?.value;
  }

  isItemValid(item: AbstractControl): boolean {
    return item.valid;
  }

  onFilesSelected(item: AbstractControl, files: any[]) {
    item.get('files')?.setValue(files);
    item.get('files')?.markAsDirty();
    this.formGroup.markAsDirty();
    item.markAllAsTouched();
  }

  setStarValue(item: AbstractControl, star: any) {
    item.get('answer')?.setValue(star);
    item.get('answer')?.markAsDirty();
    item.get('answer')?.markAsTouched();
    this.formGroup.markAsDirty();
  }


  clearResponce(item: AbstractControl) {
    item.get('answer')?.setValue(null);
    item.get('answer')?.markAsDirty();
    this.formGroup.markAsDirty();
    item.markAllAsTouched();
  }



 deleteFile(item: AbstractControl, files: any[]) {
  const control = item.get('deletedAttachmentIds');
  if (control) {
    const currentValues = control.value || [];

    control.setValue([...currentValues, ...files]);
  }
}


  private buildAnswerFromItem(item: AbstractControl): EvaluationAnswerDto {
    const question: QuestionsTask = this.getQuestionDetails(item);

    const dto: EvaluationAnswerDto = {
      taskId: this.taskId,
      questionId: question.questionId,
      textAnswer: null,
      selectedOptionId: null,
      selectedOptionIds: [],
      starRating: null,
      deletedAttachmentIds: item.get('deletedAttachmentIds')?.value ?? []
    };

    switch (question.questionTypeCode) {
      case QuestionTypeCodeEnum.Text_Area:
        dto.textAnswer = item.get('answer')?.value ?? null;
        break;

      case QuestionTypeCodeEnum.Star_Rating:
        dto.starRating = item.get('answer')?.value ?? null;
        break;

      case QuestionTypeCodeEnum.Single_Choice:
        dto.selectedOptionId = item.get('answer')?.value ?? null;
        break;

      case QuestionTypeCodeEnum.Multiple_Choice: {
        const checkedValues = this.getOptionsArray(item).value as boolean[];
        dto.selectedOptionIds = (question.questionOptions || [])
          .filter((_, i) => checkedValues[i])
          .map((opt) => opt.optionId);
        break;
      }
    }

    return dto;
  }

  private buildAnswersPayload(): EvaluationAnswerDto[] {
    return this.items.controls.map((item) => this.buildAnswerFromItem(item));
  }

  appendAttachments(formData: FormData): void {
    this.items.controls.forEach((item) => {
      const question = this.getQuestionDetails(item);
      const files = item.get('files')?.value;

      if (files && files.length) {
        files.forEach((file: any) => {
          if (file instanceof File) {
            formData.append(`attachments_${question.questionId}`, file);
          }
        });
      }
    });
  }

  saveTaskDraftAnswers() {
    this.isSubmitted = true
    const currentItem = this.items.at(this.selectedIndex);
    if (!currentItem || !currentItem.valid) {
      currentItem?.markAllAsTouched();
      this.scrollToFirstInvalidControl();
      return;
    }
    this.loading = true;
    const answers = this.buildAnswersPayload();
    const formData = new FormData();
    formData.append('data', JSON.stringify(answers));
    this.appendAttachments(formData);

    this.service.saveTaskDraftAnswers(formData).subscribe({
      next: () => {
        this.loading = false;
        this.selectedIndex = this.selectedIndex + 1;
        this.isSubmitted = false
        Swal.fire({
          title: this.translate.instant('swalMsg.savedsuccessfully'),
          imageUrl: './assets/images/icons/success.svg',
          confirmButtonText: this.translate.instant('swalMsg.Ok'),
        });
        this.dialogRef.close(true);
      },
      error: (error: any) => {
        this.loading = false;
        this.handleErrors(error.detail);
      }
    });
  }

  saveRecord() {
    this.isSubmitted = true;

    if (this.formGroup.valid) {
      this.loading = true;
      const answers = this.buildAnswersPayload();
      const formData = new FormData();
      formData.append('data', JSON.stringify(answers));
      this.appendAttachments(formData);
      this.service.saveTaskAnswers(formData).subscribe({
        next: () => {
          this.loading = false;
          Swal.fire({
            title: this.translate.instant('evaluationTasks.surveyCompletedSuccessfully'),
            text: this.translate.instant('evaluationTasks.submissionWillBeReviewed'),
            imageUrl: './assets/images/icons/success.svg',
            confirmButtonText: this.translate.instant('swalMsg.Ok'),
          });
          this.dialogRef.close(true);
        },
        error: (error: any) => {
          this.loading = false;
          this.handleErrors(error.detail);
        }
      });
    } else {
      this.formGroup.markAllAsTouched();
      this.scrollToFirstInvalidControl();
    }
  }

  handleErrors(errors: any) {
    errors = JSON.parse(errors);
    errors.ErrorDTO.forEach((error: any) => {
      this.swalService.swalFailed(error.Error);
    });
  }

  private scrollToFirstInvalidControl() {
    const firstInvalidControl: HTMLElement | null =
      this.el.nativeElement.querySelector('form .ng-invalid , form .has-error');
    firstInvalidControl?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest'
    });
  }

  handelSteps(index) {
    this.items.controls[index].markAllAsTouched();
    if (this.items.controls[index].valid) {
      this.selectedIndex = this.selectedIndex + 1;
    }
  }

  hasError(controlName: string, errorName: string, index: any = null): boolean {
    if (index !== null) {
      const itemGroup = this.items?.at(index) as FormGroup;
      if (!itemGroup || !itemGroup.controls) return false;

      const control = itemGroup.controls[controlName];
      return !!(control && control.touched && control.hasError(errorName));
    } else {
      const control = this.formGroup?.controls?.[controlName];
      return !!(control && control.hasError(errorName));
    }
  }

  questionValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const group = control as FormGroup;
      const question = group.get('questionDetails')?.value as QuestionsTask;
      const filesControl = group.get('files');

      if (!question || !filesControl) {
        return null;
      }

      let hasAnswer = false;
      switch (question.questionTypeCode) {
        case QuestionTypeCodeEnum.Text_Area:
        case QuestionTypeCodeEnum.Star_Rating:
        case QuestionTypeCodeEnum.Single_Choice:
          hasAnswer = !!group.get('answer')?.value;
          break;

        case QuestionTypeCodeEnum.Multiple_Choice:
          hasAnswer = (group.get('options')?.value as boolean[])?.some(x => x) ?? false;
          break;
      }

      const files = filesControl.value as any[];
      const hasFiles = !!files?.length;

      const filesErrors = { ...(filesControl.errors || {}) };
      delete filesErrors['required'];

      if (question.isAttachmentsRequired && hasAnswer && !hasFiles) {
        filesErrors['required'] = true;
      }
      filesControl.setErrors(Object.keys(filesErrors).length ? filesErrors : null);

      if (question.questionTypeCode === QuestionTypeCodeEnum.Multiple_Choice) {
        const forceAnswerRequired =
          question.isAttachmentsRequired && !question.isRequired && hasFiles && !hasAnswer;

        if (forceAnswerRequired || (question.isRequired && !hasAnswer)) {
          return { atLeastOneRequired: true };
        }
      }

      return null;
    };
  }

  canDeactivate(): boolean | Observable<boolean> {
    return this.formGroup.dirty;
  };

  openConfirmDialog() {
    if (this.canDeactivate()) {
      return Swal.fire({
        title: this.translate.instant('swalMsg.saveProgressPrompt'),
        // text: this.translate.instant('swalMsg.changesHappend'),
        imageUrl: './assets/images/icons/warning-ic.svg',

        showConfirmButton: true,
        confirmButtonText: this.translate.instant('swalMsg.saveProgress'),

        showDenyButton: true,
        denyButtonText: this.translate.instant('swalMsg.continueExecution'),

        showCancelButton: true,
        cancelButtonText: this.translate.instant('swalMsg.exitWithoutSaving'),

        customClass: {
          actions: 'custom-swal-actions',
          confirmButton: 'custom-swal-btn custom-confirm-btn',
          denyButton: 'custom-swal-btn custom-deny-btn',
          cancelButton: 'custom-swal-btn custom-cancel-btn'
        }
      }).then((result) => {
        if (result.isConfirmed) {
          return this.saveTaskDraftAnswers()
        } else if (result.isDenied) {
          return false
        } else {
          this.dialogRef.close();
        }
      });
    } else {
      this.dialogRef.close();
      return;
    }
  }
}
